import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/Button';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import Toast from '../../../components/ui/Toast';
import { useToast } from '../../../hooks/useToast';

const COUNTRIES_CURRENCIES = [
  { name: 'Sri Lanka', currency: 'LKR' },
  { name: 'United States', currency: 'USD' },
  { name: 'United Kingdom', currency: 'GBP' },
  { name: 'Australia', currency: 'AUD' },
  { name: 'Canada', currency: 'CAD' },
  { name: 'India', currency: 'INR' },
  { name: 'Maldives', currency: 'MVR' },
  { name: 'United Arab Emirates', currency: 'AED' },
  { name: 'Singapore', currency: 'SGD' },
  { name: 'Malaysia', currency: 'MYR' },
  { name: 'New Zealand', currency: 'NZD' },
];

export default function AdminSettings() {
  const [countries, setCountries] = useState([{ name: 'Sri Lanka', currency: 'LKR' }]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [resetText, setResetText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [claimRightsModal, setClaimRightsModal] = useState({ isOpen: false, message: '' });

  // Barber signup toggle
  const [barberSignupEnabled, setBarberSignupEnabled] = useState(true);
  const [togglingSignup, setTogglingSignup] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);

  // Admin Management
  const [admins, setAdmins] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [inviting, setInviting] = useState(false);

  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await supabase
          .from('platform_settings_1780960000000')
          .select('value')
          .eq('key', 'barber_signup_enabled')
          .maybeSingle();
        if (data) setBarberSignupEnabled(data.value === true || data.value === 'true');
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchSettings();
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    const { data } = await supabase
      .from('profiles_1740423033529')
      .select('id, full_name, email, requires_password_change')
      .eq('role', 'admin')
      .order('created_at', { ascending: false });
    if (data) setAdmins(data);
  };

  const handleToggleBarberSignup = async () => {
    setTogglingSignup(true);
    const newValue = !barberSignupEnabled;
    try {
      const { error } = await supabase
        .from('platform_settings_1780960000000')
        .upsert(
          { key: 'barber_signup_enabled', value: newValue, updated_at: new Date().toISOString() },
          { onConflict: 'key' }
        );
      if (error) throw error;
      setBarberSignupEnabled(newValue);
      showToast(`Barber registration is now ${newValue ? 'OPEN' : 'CLOSED'}`, 'success');
    } catch (err) {
      showToast('Error updating setting: ' + err.message, 'error');
    } finally {
      setTogglingSignup(false);
    }
  };

  const addCountry = () => {
    if (selectedCountry) {
      const [name, currency] = selectedCountry.split('|');
      if (!countries.find(c => c.name === name)) {
        setCountries([...countries, { name, currency }]);
        setSelectedCountry('');
        showToast("Region added successfully.", "success");
      } else {
        showToast("This country is already added.", "error");
      }
    }
  };

  const removeCountry = (countryName) => {
    setCountries(countries.filter(item => item.name !== countryName));
  };

  const handleInviteAdmin = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setInviting(true);
    setTempPassword('');
    
    // Generate a secure 10-character temporary password
    const generatedPassword = Math.random().toString(36).slice(-8) + "A1!";
    
    try {
      const { data, error } = await supabase.rpc('invite_admin_user', { 
        new_email: inviteEmail.trim().toLowerCase(), 
        temp_password: generatedPassword 
      });
      
      if (error) throw error;
      if (data && data.success === false) throw new Error(data.error);

      setTempPassword(generatedPassword);
      showToast("Admin invited successfully!", "success");
      setInviteEmail('');
      fetchAdmins();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setInviting(false);
    }
  };

  const handleReset = async () => {
    if (resetText !== 'CONFIRM') return;
    setResetting(true);
    try {
      const { data, error } = await supabase.rpc('reset_platform_data');
      const isMissingRPC = error?.message?.includes('Could not find the function');
      if (isMissingRPC) {
        throw new Error("Database is still syncing new functions. Please wait 10 seconds and try again.");
      }

      if (error || (data && data.success === false)) {
        const errorMessage = error?.message || data?.error || 'Unauthorized';
        if (errorMessage.toLowerCase().includes('unauthorized')) {
          setClaimRightsModal({ isOpen: true, message: errorMessage });
          setResetting(false);
          return;
        } else {
          throw new Error(errorMessage);
        }
      }

      showToast("Platform data has been successfully reset. Users were not deleted.", "success");
      setResetText('');
    } catch (err) {
      showToast("Error resetting data: " + err.message, "error");
    } finally {
      setResetting(false);
    }
  };

  const executeClaimRights = async () => {
    setClaimRightsModal({ isOpen: false, message: '' });
    setResetting(true);
    try {
      const claimRes = await supabase.rpc('claim_admin_rights');
      if (claimRes.error || (claimRes.data && claimRes.data.success === false)) {
        throw new Error(claimRes.error?.message || claimRes.data?.error || 'Failed to claim rights');
      }

      const retry = await supabase.rpc('reset_platform_data');
      if (retry.error || (retry.data && retry.data.success === false)) {
        throw new Error(retry.error?.message || retry.data?.error || 'Reset failed after claiming rights');
      }

      showToast("Platform data has been successfully reset.", "success");
      setResetText('');
    } catch (err) {
      showToast("Error resetting data: " + err.message, "error");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="animate-in fade-in duration-500 max-w-4xl pb-20 relative">
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-brand-primary">Platform Settings</h1>
        <p className="text-gray-500">Manage global configurations and regional access.</p>
      </div>

      <div className="grid grid-cols-1 gap-8">

        {/* Admin Management Section */}
        <section className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
              <SafeIcon name="Shield" className="text-purple-600 text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-brand-black">Platform Administrators</h2>
              <p className="text-sm text-gray-500">Invite new admins and manage access.</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
              <h3 className="text-sm font-bold text-brand-dark mb-4">Invite New Admin</h3>
              <form onSubmit={handleInviteAdmin} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Admin Email Address"
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-brand-primary bg-white font-bold text-sm"
                />
                <Button type="submit" disabled={inviting} className="px-6 whitespace-nowrap">
                  {inviting ? 'Inviting...' : 'Send Invite'}
                </Button>
              </form>

              {tempPassword && (
                <div className="mt-4 p-5 bg-green-50 border border-green-200 rounded-xl flex flex-col gap-2 animate-in fade-in slide-in-from-top-2">
                  <div className="flex items-center gap-2 text-green-800 font-bold text-sm">
                    <SafeIcon name="CheckCircle" className="text-green-600" />
                    Admin Created Successfully!
                  </div>
                  <p className="text-xs text-green-700">
                    The new admin can log in immediately at <strong>{window.location.origin}/#/login</strong>.
                    Please share this temporary password securely. They will be forced to change it on their first login.
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <code className="px-5 py-3 bg-white border border-green-200 rounded-lg font-mono text-brand-dark font-black tracking-widest text-lg shadow-sm">
                      {tempPassword}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(tempPassword);
                        showToast("Password copied to clipboard!", "success");
                      }}
                      className="text-xs font-black uppercase tracking-widest border-green-200 text-green-700 hover:bg-green-100 h-full py-3"
                    >
                      Copy Password
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Current Admins</h3>
              <div className="space-y-3">
                {admins.map(admin => (
                  <div key={admin.id} className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                    <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center text-brand-primary font-black uppercase">
                      {admin.full_name?.charAt(0) || admin.email.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-brand-dark text-sm">{admin.full_name || 'Admin User'}</p>
                      <p className="text-xs text-gray-500">{admin.email}</p>
                    </div>
                    {admin.requires_password_change && (
                      <span className="ml-auto text-[10px] bg-orange-50 text-orange-600 px-3 py-1 rounded-md font-bold uppercase tracking-widest border border-orange-100">
                        Pending Setup
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Barber Signup Toggle */}
        <section className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center">
              <SafeIcon name="Scissors" className="text-brand-primary text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-brand-black">Barber Registrations</h2>
              <p className="text-sm text-gray-500">Control whether new barbers can sign up to the platform.</p>
            </div>
          </div>

          {loadingSettings ? (
            <div className="h-16 bg-gray-50 rounded-2xl animate-pulse"></div>
          ) : (
            <div className="flex items-center justify-between p-6 bg-gray-50 rounded-2xl border border-gray-100">
              <div>
                <p className="font-black text-brand-dark text-lg">
                  New Barber Sign-ups
                </p>
                <p className="text-sm text-gray-500 font-medium mt-1">
                  {barberSignupEnabled ? 'Barbers can currently register and join the platform.' : 'New barber registrations are currently blocked.'}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <span className={`text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full border ${barberSignupEnabled ? 'bg-green-50 text-green-600 border-green-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                  {barberSignupEnabled ? 'Open' : 'Closed'}
                </span>
                <button
                  onClick={handleToggleBarberSignup}
                  disabled={togglingSignup}
                  className={`relative w-16 h-8 rounded-full transition-colors duration-300 focus:outline-none disabled:opacity-50 ${barberSignupEnabled ? 'bg-brand-primary' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-all duration-300 ${barberSignupEnabled ? 'left-9' : 'left-1'}`} />
                </button>
              </div>
            </div>
          )}
          <div className="mt-4 p-4 bg-blue-50 rounded-2xl border border-blue-100 flex gap-3">
            <SafeIcon name="Info" className="text-brand-primary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700 leading-relaxed">
              <strong>Note:</strong> Disabling this will prevent new barbers from completing their sign up. Existing barbers are not affected. The login page will still be accessible to everyone.
            </p>
          </div>
        </section>

        {/* Regional Access */}
        <section className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center">
              <SafeIcon name="Globe" className="text-brand-primary text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-brand-black">Regional Access Control</h2>
              <p className="text-sm text-gray-500">Allowed countries and their operating currencies.</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
              {countries.map(c => (
                <div key={c.name} className="flex items-center gap-2 bg-brand-primary/5 text-brand-primary px-4 py-2 rounded-full font-bold text-sm border border-brand-primary/10">
                  {c.name} <span className="bg-brand-primary text-white text-[10px] px-2 py-0.5 rounded-full">{c.currency}</span>
                  <button onClick={() => removeCountry(c.name)} className="hover:text-red-500 transition-colors ml-1">
                    <SafeIcon name="X" className="text-xs" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="flex-[3] px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-brand-primary/10 bg-white font-bold text-brand-dark"
              >
                <option value="">Select Country & Currency...</option>
                {COUNTRIES_CURRENCIES.map(c => (
                  <option key={c.name} value={`${c.name}|${c.currency}`}>{c.name} ({c.currency})</option>
                ))}
              </select>
              <Button onClick={addCountry} disabled={!selectedCountry} className="flex-1">Add Region</Button>
            </div>

            <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 flex gap-3">
              <SafeIcon name="Info" className="text-brand-primary mt-1" />
              <p className="text-xs text-blue-700 leading-relaxed">
                <strong>Localization:</strong> Users from countries not in this list will be restricted from booking services. Adding a country associates it with its local currency for the Barber's catalog.
              </p>
            </div>
          </div>
        </section>

        {/* Payment Gateways */}
        <section className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center">
              <SafeIcon name="CreditCard" className="text-brand-primary text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-brand-black">Payment Gateways</h2>
              <p className="text-sm text-gray-500">Manage how customers pay for services.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-5 border border-brand-primary bg-brand-primary/5 rounded-2xl flex items-center justify-between">
              <div className="flex items-center gap-4">
                <SafeIcon name="DollarSign" className="text-brand-primary text-xl" />
                <div>
                  <p className="font-bold">Cash After Service</p>
                  <p className="text-xs text-gray-500 tracking-tight">Direct payment to barber in Local Currency</p>
                </div>
              </div>
              <span className="text-[10px] font-black uppercase text-brand-primary bg-white px-2 py-1 rounded shadow-sm border border-brand-primary/10">Active</span>
            </div>

            <div className="p-5 border border-gray-100 bg-gray-50/50 rounded-2xl flex items-center justify-between opacity-60">
              <div className="flex items-center gap-4">
                <SafeIcon name="CreditCard" className="text-gray-400 text-xl" />
                <div>
                  <p className="font-bold text-gray-500">Stripe Integration</p>
                  <p className="text-xs text-gray-400">Card payments via Stripe API</p>
                </div>
              </div>
              <span className="text-[10px] font-black uppercase text-gray-400">Coming Soon</span>
            </div>
          </div>
        </section>

        {/* Danger Zone */}
        <section className="bg-red-50 p-8 rounded-[2rem] border border-red-100 shadow-sm mt-4">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <SafeIcon name="AlertTriangle" className="text-red-600 text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-900">Danger Zone: Factory Reset</h2>
              <p className="text-sm text-red-700">Clear all platform data (bookings, stats) to 0. This will NOT delete users.</p>
            </div>
          </div>

          <div className="space-y-4 max-w-md">
            <p className="text-xs text-red-800 font-bold">Type <strong className="text-red-900">CONFIRM</strong> below to proceed with the reset.</p>
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="CONFIRM"
                value={resetText}
                onChange={(e) => setResetText(e.target.value)}
                className="flex-1 px-4 py-3 border border-red-200 rounded-xl outline-none focus:ring-2 focus:ring-red-500 bg-white font-bold"
              />
              <Button
                variant="danger"
                disabled={resetText !== 'CONFIRM' || resetting}
                onClick={handleReset}
              >
                {resetting ? 'Resetting...' : 'Reset Data'}
              </Button>
            </div>
          </div>
        </section>
      </div>

      {/* Claim Rights Modal */}
      {claimRightsModal.isOpen && (
        <div className="fixed inset-0 bg-brand-dark/60 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] w-full max-w-sm p-8 shadow-2xl text-center">
            <div className="w-16 h-16 bg-orange-50 text-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon name="Shield" className="text-3xl" />
            </div>
            <h3 className="text-xl font-black text-brand-dark mb-2">Claim Admin Rights?</h3>
            <p className="text-gray-500 text-sm mb-4 leading-relaxed">Reset failed: {claimRightsModal.message}</p>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">Do you want to claim Admin rights now and try the reset again?</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setClaimRightsModal({ isOpen: false, message: '' })}>Cancel</Button>
              <Button className="flex-1" onClick={executeClaimRights}>Claim & Reset</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}